import type { CSSProperties } from 'react';
import './etiqueta.css';

export type EtiquetaProps = {
  /** O nome do catálogo. Sempre à vista; vive em `title` para o caso de cortar. */
  nome: string;
  /** Cor do catálogo da revenda (hex). Sem cor, a etiqueta fica neutra. */
  cor?: string | null;
  /** Um degrau menor, para linha de altura fixa: fila, lista, cabeçalho. */
  miuda?: boolean;
};

/**
 * A cor é clara o bastante para pedir texto escuro?
 *
 * Luminância relativa (WCAG) do hex. Acima de 0,45 o branco já não dá 4,5:1
 * — é o caso do amarelo, do laranja e dos pastéis — e o texto vira o escuro
 * de `--sobre-laranja`. Hex malformado cai no "escura": o branco é o padrão
 * para as cores saturadas, que são a maioria de qualquer catálogo.
 */
export function corClara(hex: string): boolean {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return false;
  const canal = (i: number) => {
    const c = parseInt(m[1]!.slice(i, i + 2), 16) / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };
  const lum = 0.2126 * canal(0) + 0.7152 * canal(2) + 0.0722 * canal(4);
  return lum > 0.45;
}

/**
 * A cor vem por `style` porque o catálogo é de cada revenda.
 *
 * Uma variável só, `--cor`: a folha pinta a pílula com ela. Antes eram três
 * declarações (`color`, `borderColor`, `background`) escritas aqui e
 * repetidas à mão em cada tela que montava a pastilha sem o componente — e
 * cada cópia envelhecia sozinha.
 */
export function Etiqueta({ cor, nome, miuda = false }: EtiquetaProps) {
  const estilo = cor ? ({ '--cor': cor } as CSSProperties) : undefined;
  return (
    <span
      className={miuda ? 'etiqueta etiqueta--miuda' : 'etiqueta'}
      style={estilo}
      data-clara={cor && corClara(cor) ? 'sim' : undefined}
      /*
       * `title` porque o nome pode estar cortado com reticências numa linha
       * densa; `aria-label` para o leitor de tela anunciar o nome inteiro.
       */
      title={nome}
      aria-label={nome}
    >
      <span className="etiqueta__nome">{nome}</span>
    </span>
  );
}

export type EtiquetasProps = {
  /** Todas as etiquetas da conversa. */
  itens: { nome: string; cor?: string | null }[];
  /** Teto do que aparece; o excedente vira "+N". */
  limite?: number;
  /** Sem quebra de linha: para linha de lista de altura fixa. */
  emLinha?: boolean;
  /**
   * Tamanho miúdo. O padrão acompanha `emLinha`: quem não quebra linha é
   * justamente quem está numa linha de altura fixa, e pedir as duas coisas
   * separadamente só criava a chance de esquecer uma.
   */
  miuda?: boolean;
};

export function Etiquetas({ itens, limite, emLinha = false, miuda = emLinha }: EtiquetasProps) {
  const visiveis = limite ? itens.slice(0, limite) : itens;
  const resto = itens.length - visiveis.length;
  return (
    <span className={emLinha ? 'etiquetas etiquetas--linha' : 'etiquetas'}>
      {visiveis.map((item) => (
        <Etiqueta key={item.nome} nome={item.nome} cor={item.cor} miuda={miuda} />
      ))}
      {resto > 0 ? (
        <span
          className={
            miuda ? 'etiqueta etiqueta--miuda etiqueta--resto' : 'etiqueta etiqueta--resto'
          }
          title={itens
            .slice(visiveis.length)
            .map((i) => i.nome)
            .join(', ')}
        >
          +{resto}
        </span>
      ) : null}
    </span>
  );
}
